import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Per-user feature switches, read live from the server rather than the JWT —
  // otherwise an Admin turning a feature off would not apply until that user's
  // next login. Default everything ON so nothing disappears while this loads.
  const [features, setFeatures] = useState({ repeat_orders: true, cafeteria: true });

  useEffect(() => {
    if (token) {
      // Decode token to get user info (simple base64 decode for demo)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.user_id,
          email: payload.sub,
          role: payload.role,
          name: payload.name,
          super_admin: payload.super_admin === true
        });
      } catch (e) {
        localStorage.removeItem('token');
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (!token) {
      setFeatures({ repeat_orders: true, cafeteria: true });
      return;
    }
    api.get('/me/features', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setFeatures((f) => ({ ...f, ...(res.data || {}) })))
      .catch(() => { /* leave the defaults — a failed check must not hide features */ });
  }, [token]);

  const login = async (email, password, authSource = '') => {
    try {
      const body = {
        username: email,
        password: password
      };
      if (authSource) {
        body.auth_source = authSource;
      }

      const response = await api.post('/auth/token', body);
      
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('token', newToken);
      
      // Decode token to get user info
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      setUser({
        id: payload.user_id,
        email: payload.sub,
        role: payload.role,
        name: payload.name,
        super_admin: payload.super_admin === true
      });

      return { success: true, token: newToken };
    } catch (error) {
      const apiError = error.response?.data;
      return { 
        success: false, 
        // Prefer detailed backend message (e.g. AD diagnostics) when available
        error: apiError?.details || apiError?.error || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Only logout if we actually have a token to clear, to avoid loops
          if (localStorage.getItem('token')) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  const value = {
    user,
    token,
    loading,
    features,
    login,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'admin',
    isPM: user?.role === 'pm',
    isTL: user?.role && ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead'].includes(user.role),
    isProduction: user?.role && ['preflight', 'cmd', 'prepress', 'preflight_prepress'].includes(user.role),
    isQC: user?.role && ['qc', 'qc_lead'].includes(user.role),
    isUser: user?.role && ['preflight', 'cmd', 'prepress', 'preflight_prepress', 'qc'].includes(user.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};